import React, { useState, useEffect, useRef } from 'react';
import Papa from 'papaparse';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { 
    getEmployeesForServerAPI, 
    getAdminsForServerAPI,
    getServerDetailsAPI, 
    toggleUserStatusAPI,
    removeUserFromServerAPI,
    fetchServersAPI,
    bulkCloneStaffAPI,
    deleteUserAPI
} from '../services/serverService';
import AddEmployeeModal from '../components/AddEmployeeModal';
import BulkUploadModal from '../components/BulkUploadModal';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../config/permissions';

const EmployeeListPage = () => {
    const { userRole } = useAuth();
    
    const { serverId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [employees, setEmployees] = useState([]);
    const [serverData, setServerData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const [modalState, setModalState] = useState(null);
    const [currentLimits, setCurrentLimits] = useState(null);

    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [isCloning, setIsCloning] = useState(false);
    const [showCloneModal, setShowCloneModal] = useState(false);
    const [availableServers, setAvailableServers] = useState([]);
    const [targetServerIds, setTargetServerIds] = useState([]);
    const [showBulkUpload, setShowBulkUpload] = useState(false);

    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
    const [bulkDeleteReason, setBulkDeleteReason] = useState('');
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);

    // Sorting State
    const [sortOrder, setSortOrder] = useState('desc'); // 'desc' (newest first) or 'asc' (oldest first)

    const fileInputRef = useRef(null);

    const handleSelectMultipleUsers = (event) => {
        const inputElement = event.target;
        const file = inputElement.files[0];
        if (!file) return;

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                const data = results.data;
                const matchedIds = [];
                let matchCount = 0;
                let notFoundCount = 0;

                const possibleNameColumns = ['fullname', 'name', 'employee name', 'employee'];
                let nameKey = null;

                if (data.length > 0) {
                    const keys = Object.keys(data[0]);
                    nameKey = keys.find(k => possibleNameColumns.includes(k.toLowerCase().trim()));
                }

                if (!nameKey && data.length > 0) {
                    nameKey = Object.keys(data[0])[0];
                }

                if (nameKey) {
                    data.forEach(row => {
                        const cellValue = row[nameKey];
                        if (cellValue) {
                            const searchName = cellValue.toString().toLowerCase().trim();
                            const matchedEmployee = employees.find(emp => 
                                emp.fullName && emp.fullName.toLowerCase().trim() === searchName
                            );
                            if (matchedEmployee) {
                                matchedIds.push(matchedEmployee.username || matchedEmployee.id);
                                matchCount++;
                            } else {
                                notFoundCount++;
                            }
                        }
                    });

                    setSelectedUserIds(prev => Array.from(new Set([...prev, ...matchedIds])));
                    
                    alert(`Successfully selected ${matchCount} users.\n${notFoundCount > 0 ? `Could not find ${notFoundCount} names from the CSV in the current list.` : ''}`);
                } else {
                    alert("Could not detect a valid name column in the CSV.");
                }

                inputElement.value = null;
            },
            error: (error) => {
                console.error("Error parsing CSV:", error);
                alert("Failed to parse CSV file.");
                inputElement.value = null;
            }
        });
    };

    const canEdit = hasPermission(userRole, 'CAN_EDIT_DATA');
    const canDelete = hasPermission(userRole, 'CAN_DELETE_DATA');

    useEffect(() => {
        if (location.state?.openAddModal) {
            setModalState('add');
            if (location.state?.currentLimits) {
                setCurrentLimits(location.state.currentLimits);
            }
            window.history.replaceState({}, document.title)
        }
    }, [location.state]);

    useEffect(() => {
        if (!serverId) return;
        fetchAllData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverId]);

    const fetchAllData = async () => {
        try {
            setIsLoading(true);
            const [employeeResponse, adminResponse, serverResponse] = await Promise.all([
                getEmployeesForServerAPI(serverId),
                getAdminsForServerAPI(serverId),
                getServerDetailsAPI(serverId)
            ]);

            const currentEmployees = employeeResponse.data.data;
            const currentAdmins = adminResponse.data.data;
            const currentServer = serverResponse.data.data;

            setServerData(currentServer);
            setEmployees(currentEmployees);

            // Default to descending sort if not already applied by backend, though backend usually orders it.
            // We'll manage the display sort in the render cycle.
            
            const isUserActive = (u) => u.active !== undefined ? u.active === true : u.status === true;
            
            const activeE = currentEmployees.filter(isUserActive).length;
            const activeA = currentAdmins.filter(isUserActive).length;

            setCurrentLimits({
                currentCount: activeE + activeA,
                maxLimit: currentServer.maxUsers || 0 
            });

        } catch (error) {
            console.error("Failed to fetch page data", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseModal = () => setModalState(null);

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const visibleIds = employees.map(u => u.username || u.id);
            setSelectedUserIds(visibleIds);
        } else setSelectedUserIds([]);
    };

    const handleSelectRow = (userId) => {
        setSelectedUserIds(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
    };

    const handleOpenCloneModal = async () => {
        if (selectedUserIds.length === 0) return alert("Select at least one user to clone.");
        setIsLoading(true);
        try {
            const response = await fetchServersAPI();
            let serversArray = [];
            const payload = response.data;
            if (Array.isArray(payload)) serversArray = payload;
            else if (payload?.data && Array.isArray(payload.data)) serversArray = payload.data;
            else if (payload?.servers && Array.isArray(payload.servers)) serversArray = payload.servers;
            else if (payload?.data?.servers && Array.isArray(payload.data.servers)) serversArray = payload.data.servers;

            if (!Array.isArray(serversArray)) throw new Error("Could not parse server list.");

            const servers = serversArray.filter(s => (s.server_id || s.id) !== serverId);
            setAvailableServers(servers);
            setTargetServerIds([]);
            setShowCloneModal(true);
        } catch (error) { alert("Failed to load server list."); } 
        finally { setIsLoading(false); }
    };

    const executeClone = async () => {
        if (targetServerIds.length === 0) return alert("Please select at least one target server.");
        const confirmClone = window.confirm(`Clone ${selectedUserIds.length} employees to ${targetServerIds.length} server(s)?\n\nProceed?`);
        if (!confirmClone) return;

        setIsCloning(true);
        setShowCloneModal(false); 
        
        try {
            const response = await bulkCloneStaffAPI({ targetServerIds, sourceServerId: serverId, userIds: selectedUserIds, role: 'employee' });
            const { successCount, clonedUsers, errors } = response.data.data;

            if (clonedUsers.length > 0 || errors.length > 0) {
                alert(`Clone process complete. Success: ${successCount}, Skipped/Failed: ${errors.length}. Downloading Clone Report...`);
                
                const headers = ['Full Name', 'Username', 'Email', 'Target Server ID', 'Status', 'Message'];
                const rows = [];
                
                // Add successful clones
                clonedUsers.forEach(u => {
                    rows.push([`"${u.fullName}"`, `"${u.newUsername}"`, `"${u.email}"`, `"${u.targetServerId}"`, `"Success"`, `"${u.plainTextPassword}"`]);
                });
                
                // Add errors/skipped
                errors.forEach(e => {
                    rows.push([`"${e.fullName || 'Unknown'}"`, `"${e.uid}"`, `""`, `"${e.targetServerId || ''}"`, `"Skipped/Failed"`, `"${e.reason}"`]);
                });

                const csvContent = [headers.join(','), ...rows.map(e => e.join(','))].join("\n");
                
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = `Clone_Report_Employees.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else if (successCount > 0) {
                 alert(`Successfully cloned ${successCount} employees, but no report data was generated.`);
            }
            setSelectedUserIds([]);
        } catch (error) { alert("Bulk cloning failed: " + (error.message || "Internal Error")); } 
        finally { setIsCloning(false); }
    };

    const handleServerToggle = (sId) => {
        setTargetServerIds(prev => prev.includes(sId) ? prev.filter(id => id !== sId) : [...prev, sId]);
    };

    const renderCloneModal = () => (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, backdropFilter: 'blur(3px)' }}>
            <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', width: '500px', maxWidth: '90%', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', maxHeight: '80vh' }}>
                <h3 style={{ marginTop: 0, color: '#2d3748', fontSize: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '15px', marginBottom: '15px' }}>Select Destination Servers</h3>
                <p style={{ color: '#4a5568', fontSize: '15px', marginBottom: '20px' }}>
                    Choose where to clone the <strong>{selectedUserIds.length}</strong> selected employees. You can select multiple servers.
                </p>
                
                <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px', marginBottom: '20px', backgroundColor: '#f8fafc' }}>
                    {availableServers.length === 0 ? (
                        <p style={{ textAlign: 'center', color: '#a0aec0', padding: '20px 0' }}>No other servers available.</p>
                    ) : (
                        availableServers.map(s => {
                            const sId = s.server_id || s.id;
                            const sName = s.companyName || s.server_name || s.name || sId;
                            const isSelected = targetServerIds.includes(sId);
                            return (
                                <label key={sId} style={{ display: 'flex', alignItems: 'center', padding: '12px', borderBottom: '1px solid #edf2f7', cursor: 'pointer', backgroundColor: isSelected ? '#ebf8ff' : 'transparent', transition: 'background-color 0.2s', borderRadius: '4px' }}>
                                    <input 
                                        type="checkbox" 
                                        checked={isSelected} 
                                        onChange={() => handleServerToggle(sId)} 
                                        style={{ marginRight: '15px', width: '18px', height: '18px', cursor: 'pointer' }}
                                    />
                                    <span style={{ fontSize: '16px', color: isSelected ? '#2b6cb0' : '#4a5568', fontWeight: isSelected ? '600' : 'normal' }}>{sName}</span>
                                </label>
                            );
                        })
                    )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '15px', borderTop: '1px solid #e2e8f0' }}>
                    <button onClick={() => setShowCloneModal(false)} style={{ padding: '10px 20px', border: '1px solid #cbd5e0', background: 'white', color: '#4a5568', borderRadius: '6px', cursor: 'pointer', fontSize: '15px', fontWeight: '500' }}>Cancel</button>
                    <button onClick={executeClone} disabled={targetServerIds.length === 0} style={{ padding: '10px 20px', border: 'none', background: targetServerIds.length > 0 ? '#3182ce' : '#a0aec0', color: 'white', borderRadius: '6px', cursor: targetServerIds.length > 0 ? 'pointer' : 'not-allowed', fontSize: '15px', fontWeight: 'bold', boxShadow: targetServerIds.length > 0 ? '0 2px 4px rgba(49, 130, 206, 0.4)' : 'none' }}>
                        Clone to {targetServerIds.length} Server{targetServerIds.length !== 1 ? 's' : ''}
                    </button>
                </div>
            </div>
        </div>
    );

    const handleToggleStatus = async (employee) => {
        const isActiveUser = employee.active !== undefined ? employee.active === true : employee.status === true;
        const actionText = isActiveUser ? "disable" : "enable";
        const reason = window.prompt(`Are you sure you want to ${actionText} ${employee.fullName}?\n\nPlease enter a reason for the Audit Log (Required):`);
        
        if (reason === null) return; 
        if (reason.trim() === "") return alert("Action cancelled: A reason is required.");

        try {
            const targetUserId = employee.username || employee.id || employee.uid || employee.email;
            await toggleUserStatusAPI({ 
                userId: targetUserId, 
                uid: employee.uid || targetUserId, 
                serverId: serverId, 
                role: 'employee',
                reason: reason.trim() 
            });
            fetchAllData();
        } catch (error) {
            console.error("Failed to toggle employee status", error);
            alert("Could not update status.");
        }
    };

    const handleRemoveUser = async (employee) => {
        const reason = window.prompt(`Are you sure you want to unlink ${employee.fullName} from this server?\n\nThis will NOT delete their global account.\nPlease enter a reason (Required):`);
        
        if (reason === null) return;
        if (reason.trim() === "") return alert("Action cancelled: A reason is required.");

        try {
            const targetUserId = employee.username || employee.id || employee.uid || employee.email;
            await removeUserFromServerAPI({ userId: targetUserId, serverId, role: 'employee', reason: reason.trim() });
            fetchAllData();
        } catch (error) {
            console.error("Failed to remove user from server", error);
            alert("Could not remove user.");
        }
    };

    const downloadBulkReport = (results, selectedIds, listData, actionType) => {
        const headers = ["Name,ID,Status,Deleted From Servers,Error Reason"];
        const rows = results.map((r, index) => {
            const selectedId = selectedIds[index];
            const userObj = listData.find(u => (u.username || u.id) === selectedId);
            const name = userObj?.fullName || 'Unknown';
            const uid = userObj?.uid || userObj?.id || selectedId;
            
            if (r.status === 'fulfilled') {
                const deletedServers = r.value?.data?.deletedFromServers?.join(';') || '';
                return `"${name}","${uid}","Success","${deletedServers}",""`;
            } else {
                const error = r.reason?.response?.data?.message || r.reason?.message || "Unknown error";
                return `"${name}","${uid}","Failed","","${error}"`;
            }
        });
        
        const csvContent = "data:text/csv;charset=utf-8," + headers.concat(rows).join("\n");
        const encodedUri = encodeURI(csvContent);
        const a = document.createElement("a");
        a.href = encodedUri;
        a.download = `bulk_${actionType}_report_${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const handleBulkLocalDelete = async () => {
        if (!bulkDeleteReason.trim()) return alert("Reason is required.");
        const confirmBulk = window.confirm(`Are you sure you want to unlink ${selectedUserIds.length} employees from this server?\nThis will NOT delete their global profiles.`);
        if (!confirmBulk) return;
        
        setIsBulkDeleting(true);
        try {
            const results = await Promise.allSettled(selectedUserIds.map(selectedId => {
                const userObj = employees.find(u => (u.username || u.id) === selectedId);
                const targetUid = userObj?.uid || userObj?.id || selectedId;
                return removeUserFromServerAPI({ 
                    uid: targetUid, 
                    userId: targetUid, 
                    serverId, 
                    role: 'employee', 
                    reason: bulkDeleteReason.trim() 
                });
            }));
            
            let failedCount = 0;
            const newSelectedIds = [];
            results.forEach((r, index) => {
                if (r.status === 'rejected') {
                    failedCount++;
                    newSelectedIds.push(selectedUserIds[index]);
                }
            });
            
            downloadBulkReport(results, selectedUserIds, employees, "unlink");
            
            if (failedCount > 0) {
                console.error("Some bulk local deletions failed.");
                alert(`Successfully unlinked ${results.length - failedCount} employees, but ${failedCount} failed. A detailed report has been downloaded. The failed employees remain selected.`);
            } else {
                alert("Employees successfully unlinked from this server. A detailed report has been downloaded.");
            }

            fetchAllData();
            setSelectedUserIds(newSelectedIds);
            setShowBulkDeleteModal(false);
            setBulkDeleteReason('');
        } catch (error) {
            console.error("Unexpected error in bulk local delete:", error);
            alert("Error during bulk remove: " + error.message);
        } finally {
            setIsBulkDeleting(false);
        }
    };

    const handleBulkGlobalDelete = async () => {
        if (!bulkDeleteReason.trim()) return alert("Reason is required.");
        const confirmBulk = window.confirm(`WARNING: Are you sure you want to PERMANENTLY DELETE ${selectedUserIds.length} employees globally? This cannot be undone.`);
        if (!confirmBulk) return;
        
        setIsBulkDeleting(true);
        try {
            const results = await Promise.allSettled(selectedUserIds.map(selectedId => {
                const userObj = employees.find(u => (u.username || u.id) === selectedId);
                const targetUid = userObj?.uid || userObj?.id || selectedId;
                return deleteUserAPI({ 
                    uid: targetUid, 
                    userId: targetUid, 
                    serverId: serverId,
                    globalDelete: true,
                    role: 'employee', 
                    reason: bulkDeleteReason.trim() 
                });
            }));
            
            let failedCount = 0;
            const newSelectedIds = [];
            results.forEach((r, index) => {
                if (r.status === 'rejected') {
                    failedCount++;
                    newSelectedIds.push(selectedUserIds[index]);
                }
            });
            
            downloadBulkReport(results, selectedUserIds, employees, "permanently_delete");
            
            if (failedCount > 0) {
                console.error("Some bulk global deletions failed.");
                alert(`Successfully permanently deleted ${results.length - failedCount} employees, but ${failedCount} failed. A detailed report has been downloaded. The failed employees remain selected.`);
            } else {
                alert("Employees successfully permanently deleted. A detailed report has been downloaded.");
            }

            fetchAllData();
            setSelectedUserIds(newSelectedIds);
            setShowBulkDeleteModal(false);
            setBulkDeleteReason('');
        } catch (error) {
            console.error("Unexpected error in bulk global delete:", error);
            alert("Error during bulk delete: " + error.message);
        } finally {
            setIsBulkDeleting(false);
        }
    };

    const renderBulkDeleteModal = () => (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, backdropFilter: 'blur(3px)' }}>
            <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', width: '500px', maxWidth: '90%', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                
                {/* X Close Button */}
                <button 
                    onClick={() => { setShowBulkDeleteModal(false); setBulkDeleteReason(''); }} 
                    disabled={isBulkDeleting}
                    style={{ position: 'absolute', top: '15px', right: '15px', background: 'none', border: 'none', fontSize: '24px', cursor: isBulkDeleting ? 'not-allowed' : 'pointer', color: isBulkDeleting ? '#a0aec0' : '#4a5568', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', borderRadius: '50%' }}
                    title="Close"
                >
                    &times;
                </button>

                <h3 style={{ marginTop: 0, color: '#2d3748', fontSize: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '15px', marginBottom: '15px' }}>Bulk Delete Employees</h3>
                <p style={{ color: '#4a5568', fontSize: '15px', marginBottom: '20px' }}>
                    You have selected <strong>{selectedUserIds.length}</strong> employee(s). Please provide a reason and choose how you want to delete them.
                </p>
                
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>Reason for Deletion <span style={{ color: '#e53e3e' }}>*</span></label>
                    <input 
                        type="text" 
                        value={bulkDeleteReason}
                        onChange={(e) => setBulkDeleteReason(e.target.value)}
                        placeholder="Required for audit logs"
                        style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e0', boxSizing: 'border-box' }}
                    />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', paddingTop: '15px', borderTop: '1px solid #e2e8f0' }}>
                    {canDelete && (
                        <button onClick={handleBulkLocalDelete} disabled={isBulkDeleting || !bulkDeleteReason.trim()} style={{ padding: '10px 20px', border: 'none', background: !bulkDeleteReason.trim() ? '#fbd38d' : '#dd6b20', color: 'white', borderRadius: '6px', cursor: !bulkDeleteReason.trim() ? 'not-allowed' : 'pointer', fontSize: '15px', fontWeight: 'bold' }}>
                            {isBulkDeleting ? 'Processing...' : 'Delete Local Profile Only (Unlink)'}
                        </button>
                    )}
                    {canDelete && (
                        <button onClick={handleBulkGlobalDelete} disabled={isBulkDeleting || !bulkDeleteReason.trim()} style={{ padding: '10px 20px', border: 'none', background: !bulkDeleteReason.trim() ? '#feb2b2' : '#e53e3e', color: 'white', borderRadius: '6px', cursor: !bulkDeleteReason.trim() ? 'not-allowed' : 'pointer', fontSize: '15px', fontWeight: 'bold' }}>
                            {isBulkDeleting ? 'Processing...' : 'Delete Global Profile (Permanently Delete)'}
                        </button>
                    )}
                    <button onClick={() => { setShowBulkDeleteModal(false); setBulkDeleteReason(''); }} disabled={isBulkDeleting} style={{ padding: '10px 20px', border: '1px solid #cbd5e0', background: 'white', color: '#4a5568', borderRadius: '6px', cursor: isBulkDeleting ? 'not-allowed' : 'pointer', fontSize: '15px', fontWeight: '500', marginTop: '10px' }}>Cancel</button>
                </div>
            </div>
        </div>
    );

    const handleDeleteUser = async (employee) => {
        const confirm1 = window.confirm(`WARNING: Are you sure you want to PERMANENTLY DELETE ${employee.fullName}?`);
        if (!confirm1) return;
        
        const reason = window.prompt("This will PERMANENTLY DELETE them from THIS server ONLY.\nPlease enter a reason (Required):");
        
        if (reason === null) return;
        if (reason.trim() === "") return alert("Action cancelled: A reason is required.");

        try {
            const targetUserId = employee.username || employee.id || employee.uid || employee.email;
            await deleteUserAPI({ userId: targetUserId, serverId: serverId, globalDelete: true, role: 'employee', reason: reason.trim() });
            fetchAllData();
            alert("User permanently deleted.");
        } catch (error) {
            console.error("Failed to delete user", error);
            alert("Error deleting user: " + (error.response?.data?.message || error.message));
        }
    };
    
    const handleExportCSV = () => {
        const selectedUsers = employees.filter(emp => selectedUserIds.includes(emp.username || emp.id));
        if (selectedUsers.length === 0) return;

        const csvData = selectedUsers.map(emp => ({
            FullName: emp.fullName || '',
            Email: emp.email || '',
            PhoneCode: emp.phoneCode || '',
            PhoneNumber: emp.phoneNumber || '',
            Role: emp.role || '',
            Username: emp.username || '',
            Passcode: emp.passcode || '',
            Status: emp.active !== undefined ? (emp.active ? 'Active' : 'Inactive') : (emp.status ? 'Active' : 'Inactive'),
            Address: emp.address ? `${emp.address.street || ''} ${emp.address.city || ''} ${emp.address.county || ''} ${emp.address.postCode || ''}`.trim() : ''
        }));

        const csv = Papa.unparse(csvData);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `employees_export_${new Date().toISOString().slice(0,10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    
    return (
        <div className="page-container">
            {modalState && (
                <AddEmployeeModal serverId={serverId} closeModal={handleCloseModal} refreshList={fetchAllData} employeeToEdit={modalState !== 'add' ? modalState : null} currentLimits={currentLimits} existingUsers={employees} />
            )}

            {showCloneModal && renderCloneModal()}

            {showBulkUpload && (
                <BulkUploadModal 
                    serverId={serverId} 
                    role="employee"
                    closeModal={() => setShowBulkUpload(false)} 
                    refreshList={fetchAllData} 
                />
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', backgroundColor: 'white', padding: '20px 25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', gap: '15px' }}>
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                    <button onClick={() => navigate(`/servers/${serverId}`)} style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', background: 'white', cursor: 'pointer', color: '#4a5568', fontWeight: '500' }}>← Back</button>
                    <h1 style={{ margin: 0, fontSize: '18px', color: '#2c3e50', fontWeight: '600' }}>Employees List for <span style={{ color: '#0056b3' }}>{serverData?.companyName || 'Server'}</span></h1>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginRight: '6px' }}>
                        <span style={{ fontSize: '13px', color: '#4a5568', fontWeight: '500' }}>Sort by:</span>
                        <select 
                            value={sortOrder} 
                            onChange={(e) => setSortOrder(e.target.value)}
                            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e0', backgroundColor: 'white', color: '#2d3748', cursor: 'pointer', outline: 'none', fontSize: '13px' }}
                        >
                            <option value="desc">Newest First</option>
                            <option value="asc">Oldest First</option>
                        </select>
                    </div>
                    <div style={{ padding: '6px 12px', backgroundColor: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: '6px', color: '#4a5568', fontWeight: '600', fontSize: '13px', marginRight: '5px' }}>
                        Active: <span style={{ color: '#48bb78' }}>{employees.filter(e => e.active !== undefined ? e.active === true : e.status === true).length}</span> / {employees.length}
                    </div>
                    {canEdit && (
                        <>
                            <input 
                                type="file" 
                                accept=".csv" 
                                ref={fileInputRef} 
                                style={{ display: 'none' }} 
                                onChange={handleSelectMultipleUsers} 
                            />
                            <button onClick={() => fileInputRef.current.click()} style={{ padding: '8px 12px', backgroundColor: 'white', color: '#38a169', border: '1px solid #38a169', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>Select multiple users</button>
                            <button onClick={() => setModalState('add')} style={{ padding: '8px 12px', backgroundColor: '#0056b3', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>+ Add New Employee</button>
                            <button onClick={() => setShowBulkUpload(true)} style={{ padding: '8px 12px', backgroundColor: 'white', color: '#0056b3', border: '1px solid #0056b3', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>Bulk Import via CSV</button>
                        </>
                    )}
                </div>
            </div>

            {selectedUserIds.length > 0 && (canDelete || canEdit) && (
                <div style={{ backgroundColor: '#ebf8ff', padding: '15px', borderRadius: '8px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #bee3f8' }}>
                    <div style={{ color: '#2b6cb0', fontWeight: 'bold' }}>{selectedUserIds.length} employee(s) selected</div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button onClick={handleExportCSV} style={{ backgroundColor: '#48bb78', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>
                            Export CSV
                        </button>
                        {canDelete && (
                            <button onClick={() => setShowBulkDeleteModal(true)} style={{ backgroundColor: '#e53e3e', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 2px 4px rgba(229, 62, 62, 0.2)' }}>
                                Bulk Delete
                            </button>
                        )}
                        {canEdit && (
                            <button onClick={handleOpenCloneModal} disabled={isCloning} style={{ backgroundColor: '#3182ce', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', fontWeight: 'bold', cursor: isCloning ? 'not-allowed' : 'pointer' }}>
                                {isCloning ? 'Cloning & Generating Passwords...' : 'Clone Selected to New Server'}
                            </button>
                        )}
                    </div>
                </div>
            )}

            <div className="data-table-container">
                <table className="data-table" style={{ fontSize: '0.85rem' }}>
                    <thead>
                        <tr>
                            <th style={{ width: '40px', textAlign: 'center', padding: '0 10px', overflow: 'visible', textOverflow: 'clip', verticalAlign: 'middle' }}>
                                <input 
                                    type="checkbox"
                                    onChange={handleSelectAll}
                                    checked={employees.length > 0 && selectedUserIds.length === employees.length}
                                    style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                                />
                            </th>
                            <th>Username</th>
                            <th>Name</th>
                            <th style={{ width: '25%' }}>Email</th>
                            <th style={{ width: '12%', whiteSpace: 'nowrap' }}>Phone</th>
                            <th>Region</th>
                            <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan="7" className="no-data-cell">Loading Employees...</td></tr>
                        ) : employees.length > 0 ? (
                            [...employees]
                            .sort((a, b) => {
                                // createdAt can be a Firestore Timestamp ({_seconds, _nanoseconds}), Date string, or missing
                                const getMs = (dateObj) => {
                                    if (!dateObj) return 0;
                                    if (dateObj._seconds) return dateObj._seconds * 1000;
                                    const parsed = new Date(dateObj).getTime();
                                    return isNaN(parsed) ? 0 : parsed;
                                };
                                const timeA = getMs(a.createdAt);
                                const timeB = getMs(b.createdAt);
                                return sortOrder === 'asc' ? timeA - timeB : timeB - timeA;
                            })
                            .map((employee) => {
                                const isActiveUser = employee.active !== undefined ? employee.active === true : employee.status === true;

                                return (
                                    <tr key={employee.id} onClick={() => navigate(`/servers/${serverId}/employees/${employee.id}`)} style={{ cursor: 'pointer', backgroundColor: selectedUserIds.includes(employee.username || employee.id) ? '#f0f9ff' : 'transparent', opacity: isActiveUser ? 1 : 0.6 }}>
                                        <td style={{ textAlign: 'center', padding: '0 10px', overflow: 'visible', textOverflow: 'clip', verticalAlign: 'middle' }} onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selectedUserIds.includes(employee.username || employee.id)}
                                                onChange={() => handleSelectRow(employee.username || employee.id)}
                                                style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                                            />
                                        </td>
                                        <td style={{ verticalAlign: 'middle' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '500' }}>
                                                <span title={isActiveUser ? 'Active' : 'Suspended'} style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: isActiveUser ? '#48bb78' : '#a0aec0', display: 'inline-block', flexShrink: 0 }}></span>
                                                {employee.username}
                                            </div>
                                        </td>
                                        <td style={{ verticalAlign: 'middle' }}>{employee.fullName}</td>
                                        <td style={{ verticalAlign: 'middle', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }}>{employee.email}</td>
                                        <td style={{ verticalAlign: 'middle' }}>{employee.phoneNumber}</td>
                                        <td style={{ verticalAlign: 'middle' }}>
                                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', maxWidth: '250px' }}>
                                                {[employee.primaryRegionalTag, employee.secondaryRegionalTag].filter(Boolean).map((tag, i) => (
                                                    <span key={i} style={{ padding: '2px 6px', borderRadius: '4px', fontSize: '0.7rem', backgroundColor: '#edf2f7', border: '1px solid #e2e8f0', color: '#4a5568', whiteSpace: 'nowrap' }}>{tag}</span>
                                                ))}
                                                {!employee.primaryRegionalTag && !employee.secondaryRegionalTag && (
                                                    <span style={{ padding: '2px 6px', borderRadius: '4px', fontSize: '0.7rem', backgroundColor: '#edf2f7', border: '1px solid #e2e8f0', color: '#a0aec0' }}>None</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="actions-cell" style={{ textAlign: 'right', verticalAlign: 'middle' }}>
                                            {canEdit ? (
                                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
                                                    <button title="Edit" onClick={() => setModalState(employee)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4a5568', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                                                    </button>
                                                    <button title={isActiveUser ? 'Disable' : 'Enable'} onClick={() => handleToggleStatus(employee)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: isActiveUser ? '#d69e2e' : '#38a169', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        {isActiveUser ? (
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
                                                        ) : (
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                                                        )}
                                                    </button>
                                                    
                                                    {canDelete && (
                                                        <>
                                                            <button title="Unlink" onClick={() => handleRemoveUser(employee)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dd6b20', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18.36 6.64a9 9 0 1 1-12.73 0"></path><line x1="12" y1="2" x2="12" y2="12"></line></svg>
                                                            </button>
                                                            <button title="Delete" onClick={() => handleDeleteUser(employee)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e53e3e', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            ) : (
                                                <span style={{ color: '#999', fontSize: '0.85rem', fontStyle: 'italic' }}>View Only</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr><td colSpan="7" className="no-data-cell">No Employees Found</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            {canDelete && showBulkDeleteModal && renderBulkDeleteModal()}
        </div>
    );
};

export default EmployeeListPage;